import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'

// DS Text area spec:
//   Small  — px-2.5, py-1.5, text-xs  (12px, compact variant)
//   Medium — px-3,   py-2,   text-base (16px, default)
//   Large  — px-4,   py-2.5, text-base (16px)
//
// Placeholder colour: --text-placeholder (neutral-300, #9FAEBC) per DS colour.text.placeholder
// Removed md:text-sm — DS input value text is always text-base (16px)
const textareaVariants = cva(
  'flex w-full min-h-16 rounded-md border border-input bg-transparent text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-text-placeholder focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 read-only:bg-muted/50 read-only:cursor-default aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:ring-destructive/40',
  {
    variants: {
      size: {
        small: 'px-2.5 py-1.5 text-xs',
        medium: 'px-3 py-2 text-base',
        large: 'px-4 py-2.5 text-base',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
)

type TextareaProps = Omit<React.ComponentProps<'textarea'>, 'size'> &
  VariantProps<typeof textareaVariants> & {
    dynamicHeight?: boolean
  }

function Textarea({ className, size, dynamicHeight = false, ...props }: TextareaProps) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(textareaVariants({ size }), dynamicHeight ? 'field-sizing-content' : 'resize-y', className)}
      {...props}
    />
  )
}

export { Textarea, textareaVariants }
export type { TextareaProps }
